/**
 * Survey Says reducer. Pure function: takes a state + action, returns the next
 * state (or the same reference when nothing changed, so subscribers can skip).
 *
 * Scene routing rules:
 *   - LOBBY (no question, no podium)               -> lobby
 *   - ss_question_open                             -> prompt
 *   - first ss_answer_reveal                       -> board
 *   - ss_question_results                          -> results
 *   - ss_game_over                                 -> podium
 *   - WS connection lost                           -> connection_lost
 */

import type { OutMsg } from '@sg/shared';
import type { BrowserState, RevealedAnswer, SceneName } from './types.js';

export type Action =
  | { type: 'ws'; msg: OutMsg }
  | { type: 'connection'; value: BrowserState['connection'] };

export function initialState(): BrowserState {
  return {
    scene: 'lobby',
    connection: 'idle',
    tenantId: '',
    state: 'LOBBY',
    theme: null,
    leaderboard: [],
    leaderboardAllTime: [],
    leaderboardSession: [],
    question: null,
    results: null,
    podium: null,
  };
}

export function reduce(state: BrowserState, action: Action): BrowserState {
  if (action.type === 'connection') {
    if (state.connection === action.value) return state;
    const scene: SceneName = action.value === 'open' ? state.scene : 'connection_lost';
    return { ...state, connection: action.value, scene };
  }
  return reduceWs(state, action.msg);
}

function reduceWs(state: BrowserState, msg: OutMsg): BrowserState {
  switch (msg.type) {
    case 'state_snapshot': {
      const s = msg.state;
      // LOBBY snapshot wipes any stale question/results so the lobby renders
      // cleanly on (re)connect mid-LOBBY. RESULTS keeps the podium visible.
      const isLobby = s.state.startsWith('LOBBY');
      const isResults = s.state.startsWith('RESULTS');
      return {
        ...state,
        tenantId: s.tenant_id,
        state: s.state,
        theme: s.theme ?? state.theme,
        leaderboard: s.leaderboard_top10 ?? state.leaderboard,
        leaderboardAllTime: s.leaderboard_all_time ?? state.leaderboardAllTime,
        // LOBBY also wipes the session view (a new game restarts standings).
        leaderboardSession: isLobby ? [] : state.leaderboardSession,
        question: isLobby ? null : state.question,
        results: isLobby ? null : state.results,
        podium: isLobby ? null : state.podium,
        scene: isLobby ? 'lobby' : isResults ? 'podium' : state.scene,
      };
    }
    case 'ss_question_open': {
      return {
        ...state,
        scene: 'prompt',
        results: null,
        question: {
          surveyId: msg.survey_id,
          prompt: msg.prompt,
          index: msg.question_index,
          total: msg.questions_total,
          endsAtMs: msg.ends_at_ms,
          countdownStartsAtMs: msg.countdown_starts_at_ms,
          revealed: [],
          revealsTotal: 0,
        },
      };
    }
    case 'ss_answer_reveal': {
      const q = state.question;
      const entry: RevealedAnswer = {
        rank: msg.rank,
        answer: msg.answer,
        count: msg.count,
        points: msg.points,
        matchedCount: msg.matched_count,
      };
      const revealed = q ? [...q.revealed, entry] : [entry];
      return {
        ...state,
        scene: 'board',
        question: q
          ? { ...q, revealed, revealsTotal: msg.reveals_total }
          : {
              surveyId: msg.survey_id,
              prompt: '',
              index: 0,
              total: 1,
              endsAtMs: 0,
              countdownStartsAtMs: 0,
              revealed,
              revealsTotal: msg.reveals_total,
            },
      };
    }
    case 'ss_question_results': {
      return {
        ...state,
        scene: 'results',
        leaderboardSession: msg.session_leaderboard ?? state.leaderboardSession,
        results: {
          surveyId: msg.survey_id,
          index: msg.question_index,
          total: msg.questions_total,
          board: msg.board,
          totalPointsAwarded: msg.total_points_awarded,
        },
      };
    }
    case 'ss_game_over': {
      return {
        ...state,
        scene: 'podium',
        podium: msg.podium,
      };
    }
    default:
      return state;
  }
}
